import { useSignal } from "@preact/signals";

import type {
  ClassReference,
  FileReference,
  LoadedArtifact,
  SourceTab,
  SourceTarget,
} from "../model/types.js";
import { CodePane } from "./CodePane.js";
import { TreePane } from "./TreePane.js";

import styles from "./ApplicationWorkspace.module.css";

interface ApplicationWorkspaceProps {
  readonly artifacts: readonly LoadedArtifact[];
  readonly tabs: readonly SourceTab[];
  readonly activeTabId: string | null;
  readonly onOpenClass: (
    reference: ClassReference,
    target: SourceTarget,
    relativePath: string,
  ) => void;
  readonly onOpenFile: (reference: FileReference) => void;
  readonly onUnloadArtifact: (artifactId: string) => void;
  readonly onActivateTab: (tabId: string) => void;
  readonly onCloseTab: (tabId: string) => void;
  readonly onCloseAllTabs: () => void;
  readonly onCloseOtherTabs: (tabId: string) => void;
  readonly onCopyRelativePath: (relativePath: string) => void;
  readonly onExportDecompiledSource: (reference: ClassReference) => void;
  readonly onChooseFiles: () => void;
}

export function ApplicationWorkspace({
  artifacts,
  tabs,
  activeTabId,
  onOpenClass,
  onOpenFile,
  onUnloadArtifact,
  onActivateTab,
  onCloseTab,
  onCloseAllTabs,
  onCloseOtherTabs,
  onCopyRelativePath,
  onExportDecompiledSource,
  onChooseFiles,
}: ApplicationWorkspaceProps) {
  const treeWidth = useSignal(320);

  const startTreeResize = (event: MouseEvent): void => {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = treeWidth.value;

    const handleMouseMove = (moveEvent: MouseEvent): void => {
      const maximumWidth = Math.max(180, window.innerWidth - 240);
      treeWidth.value = Math.min(
        maximumWidth,
        Math.max(180, startWidth + moveEvent.clientX - startX),
      );
    };

    const handleMouseUp = (): void => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  return (
    <main
      class={styles.workspace}
      style={{ gridTemplateColumns: `${treeWidth.value}px 7px minmax(0, 1fr)` }}
    >
      <TreePane
        artifacts={artifacts}
        onOpenClass={onOpenClass}
        onOpenFile={onOpenFile}
        onUnloadArtifact={onUnloadArtifact}
        onCopyRelativePath={onCopyRelativePath}
        onExportDecompiledSource={onExportDecompiledSource}
      />
      <div
        class={styles.treeResizeHandle}
        title="Resize file tree"
        onMouseDown={startTreeResize}
      />
      <CodePane
        tabs={tabs}
        activeTabId={activeTabId}
        onActivate={onActivateTab}
        onClose={onCloseTab}
        onCloseAll={onCloseAllTabs}
        onCloseOthers={onCloseOtherTabs}
        onCopyRelativePath={onCopyRelativePath}
        onExportDecompiledSource={onExportDecompiledSource}
        onChooseFiles={onChooseFiles}
      />
    </main>
  );
}
